import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { queryClient } from '@/main';
import { fromApiOrganizationUserApiKeyFieldAdapter } from '@/resources/organization-user-api-key/organization-user-api-key.field-adapter';
import type { QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

export interface OpenApiSchemaDocument {
  openapi: string;
  info: {
    title: string;
    description?: string;
    version: string;
  };
  paths: Record<string, Record<string, {
    summary?: string;
    description?: string;
    operationId?: string;
    parameters?: Array<{
      name: string;
      in: string;
      required?: boolean;
      description?: string;
      schema?: Record<string, unknown>;
    }>;
    requestBody?: {
      content?: Record<string, { schema?: Record<string, unknown> }>;
    };
    responses?: Record<string, { description?: string }>;
  }>>;
  components?: {
    securitySchemes?: Record<string, {
      type: string;
      in?: string;
      name?: string;
      description?: string;
    }>;
    schemas?: Record<string, Record<string, unknown>>;
  };
  security?: Array<Record<string, unknown>>;
}

export const organizationUserApiKeyQueries = queryNamespace(Resource.organizationUserApiKey, {
  list: (params?: {
    filter?: Omit<operations['OrganizationUserApiKeyController_getMany']['parameters']['query'], 'limit' | 'page' | 'sortBy'>;
    sorting?: {
      id: string;
      desc: boolean;
    };
    paging: {
      pageIndex: number;
      pageSize: number;
    };
  }) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/api-keys', {
          signal,
          params: {
            query: {
              limit: params?.paging?.pageSize ?? 10,
              page: (params?.paging?.pageIndex ?? 0) + 1,
              sortBy: params?.sorting
                ? [ `${params.sorting.id}:${params.sorting.desc ? 'DESC' : 'ASC'}` ]
                : undefined,
              ...params?.filter,
            },
          },
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }

          return {
            data: data.items,
            totalCount: data.totalCount,
          };
        }),
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.data.map((row) => ({
        original: row,
        transformed: {
          forLabels: fromApiOrganizationUserApiKeyFieldAdapter.transform(row),
        },
        meta: {},
      })),
    }),
  }),
  integrationSchema: () => queryOptions({
    queryKey: [],
    queryFn: async ({ signal }): Promise<OpenApiSchemaDocument> => {
      const { error, response } = await client.GET('/customer/api/tcd/api-keys/schema/download', { signal, parseAs: 'stream' });
      if (error) {
        throw new HttpException({
          message: 'Failed to load integration schema',
          response,
          isGlobal: false,
        });
      }
      return response.json() as Promise<OpenApiSchemaDocument>;
    },
    staleTime: Infinity,
  }),
});

export type OrganizationUSerApiKeyQueryResultData = QueryResultData<typeof organizationUserApiKeyQueries.list>['data'][number];

export const organizationUserApiKeyMutations = mutationNamespace(Resource.organizationUserApiKey, {
  create: mutationOptions({
    mutationFn: (body: components['schemas']['CreateOrganizationUserApiKeyDto']) =>
      client
        .POST('/customer/api/tcd/api-keys', { body })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: organizationUserApiKeyQueries.all.queryKey }),
  }),
  downloadSchema: mutationOptions({
    mutationFn: async () => {
      const { data, error, response } = await client.GET('/customer/api/tcd/api-keys/schema/download', { parseAs: 'blob' });

      if (error) {
        throw new HttpException({
          message: 'Failed to download integration schema',
          response,
          isGlobal: false,
        });
      }

      return data;
    },
  }),
  update: mutationOptions({
    mutationFn: ({ apiKey, body }: { apiKey: string; body: components['schemas']['UpdateOrganizationUserApiKeyDto'] }) =>
      client
        .PATCH('/customer/api/tcd/api-keys/{apiKey}', { params: { path: { apiKey, id: 0 }}, body })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onMutate: async ({ apiKey, body }) => {
      await queryClient.cancelQueries({ queryKey: organizationUserApiKeyQueries.all.queryKey });

      const previousLists = queryClient.getQueriesData<{ data: components['schemas']['OrganizationUserApiKeyResponseDto'][]; totalCount: number }>({
        queryKey: organizationUserApiKeyQueries.all.queryKey,
      });

      queryClient.setQueriesData<{ data: components['schemas']['OrganizationUserApiKeyResponseDto'][]; totalCount: number }>(
        { queryKey: organizationUserApiKeyQueries.all.queryKey },
        (old) => old && {
          ...old,
          data: old.data.map((item) => (item.apiKey === apiKey
            ? { ...item, ...body, status: body.status as unknown as components['schemas']['OrganizationUserApiKeyStatusEnum'] }
            : item)),
        },
      );

      return { previousLists };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists?.forEach(([ queryKey, data ]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: organizationUserApiKeyQueries.all.queryKey });
    },
  }),
});

export type OrganizationUserApiKeyListQueryResult = ReturnType<typeof organizationUserApiKeyQueries.list>;
